import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import axios, { useApiRequest } from "../../../api/axios";
import {
  ERROR_SNACKBAR,
  EXPIRED_CONTRACT_ACTIONS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../../consts";
import {
  ExpiredContractAction,
  ExpiredContractListItem,
  ExpiredContractProcessPayload,
} from "../../../types/contracts";

interface ExpiredContractsActionsProps {
  contracts: ExpiredContractListItem[];
  onSuccess: () => void;
}

export const ExpiredContractsActions = ({
  contracts,
  onSuccess,
}: ExpiredContractsActionsProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const [selectedActions, setSelectedActions] = useState<
    Record<number, ExpiredContractAction | undefined>
  >({});

  const selectedCount = useMemo(
    () => Object.values(selectedActions).filter(Boolean).length,
    [selectedActions]
  );

  const handleActionSelect = (
    contractId: number,
    action: ExpiredContractAction
  ) => {
    setSelectedActions((prev) => ({
      ...prev,
      [contractId]: prev[contractId] === action ? undefined : action,
    }));
  };

  const handleSubmit = async () => {
    const payload: ExpiredContractProcessPayload = {
      indefinite_period_contract_ids: [],
      ended_contract_ids: [],
    };

    Object.entries(selectedActions).forEach(([contractId, action]) => {
      if (action === "indefinite") {
        payload.indefinite_period_contract_ids.push(Number(contractId));
      }

      if (action === "ended") {
        payload.ended_contract_ids.push(Number(contractId));
      }
    });

    if (
      payload.indefinite_period_contract_ids.length === 0 &&
      payload.ended_contract_ids.length === 0
    ) {
      enqueueSnackbar(
        t("Please choose action for at least one contract"),
        ERROR_SNACKBAR
      );
      return;
    }

    const result = await executeRequest(() =>
      axios.post(EXPIRED_CONTRACT_ACTIONS_ENDPOINT, payload)
    );

    if (result?.success) {
      enqueueSnackbar(
        t("Expired contracts processed successfully"),
        SUCCESS_SNACKBAR
      );
      onSuccess();
      return;
    }

    enqueueSnackbar(t("Failed to process expired contracts"), ERROR_SNACKBAR);
  };

  if (contracts.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t("No expired contracts to process.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t("Choose what to do with each expired contract")}
      </Typography>

      <Paper sx={{ maxHeight: { xs: "50dvh", sm: 420 }, overflow: "auto" }}>
        <List>
          {contracts.map((contract, index) => {
            const selectedAction = selectedActions[contract.id];
            const clientName = contract.client?.name || "-";

            return (
              <div key={contract.id}>
                <ListItem
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1, width: "100%" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                      {contract.number || t("No number")} - {contract.internal_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("Client")}: {clientName}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {t("End date")}: {contract.end_date || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant={
                        selectedAction === "indefinite" ? "contained" : "outlined"
                      }
                      onClick={() => handleActionSelect(contract.id, "indefinite")}
                      disabled={loading}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {t("Make indefinite")}
                    </Button>
                    <Button
                      variant={selectedAction === "ended" ? "contained" : "outlined"}
                      color={selectedAction === "ended" ? "warning" : "inherit"}
                      onClick={() => handleActionSelect(contract.id, "ended")}
                      disabled={loading}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {t("End contract")}
                    </Button>
                  </Box>
                </ListItem>
                {index < contracts.length - 1 && <Divider />}
              </div>
            );
          })}
        </List>
      </Paper>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        fullWidth
      >
        {loading
          ? t("Processing...")
          : t("Submit ({{count}} selected)", { count: selectedCount })}
      </Button>
    </Box>
  );
};
